import { useEffect, useMemo, useState } from "react";

function ownerNotificationLabel(value) {
  if (!value) {
    return "Range message";
  }
  return String(value)
    .replace(/[._-]+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function notificationStatusLabel(value) {
  const labels = {
    archived: "Archived",
    failed: "Unread",
    queued: "Unread",
    reviewed: "Read",
    sent: "Sent"
  };
  return labels[value] || ownerNotificationLabel(value);
}

function chapterNotificationStatusLabel(value) {
  const labels = {
    archived: "Archived",
    failed: "Unread",
    queued: "Unread",
    reviewed: "Read",
    sent: "Sent"
  };
  return labels[value] || notificationStatusLabel(value);
}

function notificationVisualState(notification = {}) {
  const status = notification.status || "queued";
  if (status === "archived") {
    return "archived";
  }
  if (status === "reviewed") {
    return "read";
  }
  return "unread";
}

function emptyNotificationCopy(tab) {
  const copy = {
    archived: "No archived messages.",
    queued: "No unread messages.",
    reviewed: "No read messages."
  };
  return copy[tab] || "No messages match these filters.";
}

const OWNER_VISIBLE_NOTIFICATION_TYPES = new Set([
  "event.signed_off",
  "event.created_by_chapter_lead",
  "event.approval_requested",
  "membership.prequalification_submitted"
]);

const CHAPTER_VISIBLE_NOTIFICATION_TYPES = new Set([
  "chapter.member_added",
  "event.approval_decision"
]);

function shouldShowNotification(notification = {}, chapterWorkspace = false) {
  const type = String(notification.type || "").toLowerCase();
  if (chapterWorkspace) {
    return CHAPTER_VISIBLE_NOTIFICATION_TYPES.has(type);
  }
  return OWNER_VISIBLE_NOTIFICATION_TYPES.has(type);
}

function notificationIntentKey(notification = {}) {
  const type = String(notification.type || "").toLowerCase();
  const related = notification.related || {};
  const metadata = notification.metadata || {};
  const entityId = (
    related.eventId ||
    metadata.eventId ||
    related.memberId ||
    metadata.memberId ||
    related.chapterId ||
    metadata.chapterId ||
    ""
  );
  if (type && entityId) {
    return `${type}:${entityId}`;
  }
  return `${type}:${notification.subject || ""}:${notification.body || ""}` || notification.notificationId;
}

function collapseNotificationFeed(notifications = []) {
  const byIntent = new Map();
  notifications.forEach((notification) => {
    const key = notificationIntentKey(notification);
    const current = byIntent.get(key);
    if (!current) {
      byIntent.set(key, {
        ...notification,
        duplicateNotificationIds: [notification.notificationId].filter(Boolean)
      });
      return;
    }
    const currentIds = current.duplicateNotificationIds || [current.notificationId].filter(Boolean);
    byIntent.set(key, {
      ...current,
      duplicateNotificationIds: Array.from(new Set([
        ...currentIds,
        notification.notificationId
      ].filter(Boolean)))
    });
  });
  return Array.from(byIntent.values());
}

function memberNotificationHref(notification = {}, selectedRange = {}) {
  const type = String(notification.type || "").toLowerCase();
  if (type !== "chapter.member_added" || !selectedRange?.rangeId) {
    return "";
  }
  const related = notification.related || {};
  const metadata = notification.metadata || {};
  const memberId = related.memberId || metadata.memberId || "";
  const memberEmail = related.memberEmail || metadata.memberEmail || "";
  const params = new URLSearchParams();
  if (memberId) {
    params.set("memberId", memberId);
  } else if (memberEmail) {
    params.set("memberEmail", memberEmail);
  }
  return params.toString()
    ? `/workspace/ranges/${encodeURIComponent(selectedRange.rangeId)}/apps/members?${params.toString()}`
    : "";
}

function eventNotificationHref(notification = {}, selectedRange = {}, chapterWorkspace = false) {
  if (!selectedRange?.rangeId) {
    return "";
  }
  const type = String(notification.type || "").toLowerCase();
  if (!["event.signed_off", "event.created_by_chapter_lead", "event.approval_requested", "event.approval_decision"].includes(type)) {
    return "";
  }
  const eventId = notification.related?.eventId || notification.metadata?.eventId || "";
  if (!eventId) {
    return "";
  }
  const basePath = chapterWorkspace
    ? `/workspace/ranges/${encodeURIComponent(selectedRange.rangeId)}/apps/events`
    : `/console/ranges/${encodeURIComponent(selectedRange.rangeId)}/apps/events`;
  const section = type === "event.approval_requested" ? "schedule" : "roster";
  return `${basePath}?eventId=${encodeURIComponent(eventId)}#events-${section}`;
}

function ownerMembershipNotificationHref(notification = {}, selectedRange = {}) {
  const type = String(notification.type || "").toLowerCase();
  if (type !== "membership.prequalification_submitted" || !selectedRange?.rangeId) {
    return "";
  }
  const related = notification.related || {};
  const metadata = notification.metadata || {};
  const memberId = related.memberId || metadata.memberId || "";
  const params = new URLSearchParams();
  if (memberId) {
    params.set("memberId", memberId);
  }
  const query = params.toString();
  return `/console/ranges/${encodeURIComponent(selectedRange.rangeId)}/apps/membership${query ? `?${query}` : ""}#membership-reviews`;
}

function normalizeNotificationEmail(value = "") {
  return String(value || "").trim().toLowerCase();
}

function memberDisplayName(member = {}) {
  const directName = member.name || member.fullName || member.displayName || "";
  if (directName) {
    return directName;
  }
  return [member.firstName, member.lastName].filter(Boolean).join(" ");
}

function buildMemberLookup(members = []) {
  const byId = new Map();
  const byEmail = new Map();
  members.forEach((member) => {
    if (member.memberId) {
      byId.set(member.memberId, member);
    }
    const email = normalizeNotificationEmail(member.email);
    if (email) {
      byEmail.set(email, member);
    }
  });
  return { byEmail, byId };
}

function memberNotificationDetails(notification = {}, memberLookup = { byEmail: new Map(), byId: new Map() }) {
  const related = notification.related || {};
  const metadata = notification.metadata || {};
  const memberId = related.memberId || metadata.memberId || "";
  const denormalizedEmail = related.memberEmail || metadata.memberEmail || "";
  const currentMember = (memberId && memberLookup.byId.get(memberId)) ||
    (denormalizedEmail && memberLookup.byEmail.get(normalizeNotificationEmail(denormalizedEmail))) ||
    null;
  const currentName = currentMember ? memberDisplayName(currentMember) : "";
  const currentEmail = currentMember?.email || "";
  const name = currentName ||
    related.memberName ||
    related.fullName ||
    metadata.memberName ||
    metadata.fullName ||
    "";
  const email = currentEmail || denormalizedEmail || "";
  const chapter = related.chapterName || metadata.chapterName || "";
  return {
    chapter,
    email,
    name: name || email || "New member"
  };
}

function displayNotificationText(value = "") {
  return String(value || "")
    .replace(/Test Approval Flow/gi, "Seattle Skills Clinic")
    .replace(/Test Jun 12th/gi, "Seattle Chapter Skills Session")
    .replace(/^Owner review \d+$/i, "Owner review message")
    .replace(/^Owner review message \d+ for chapter readiness\.?$/i, "Chapter readiness review message")
    .replace(/Demo Saint Paul Lead Final \d{4}-\d{2}-\d{2} \d+/gi, "Saint Paul Chapter Training Session")
    .replace(/Demo Saint Paul Lead Reaudit \d{4}-\d{2}-\d{2} \d+/gi, "Saint Paul Chapter Practice Session")
    .replace(/Demo Saint Paul Handoff Drill \d{4}-\d{2}-\d{2} \d+/gi, "Saint Paul Member Handoff Session")
    .replace(/Demo Saint Paul Safety Orientation \d{4}-\d{2}-\d{2}/gi, "Saint Paul Safety Orientation")
    .replace(/Handoff Drill/gi, "Member Handoff Session");
}

function notificationDateTime(value = "") {
  if (!value) {
    return "";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  return date.toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit"
  });
}

function notificationRelativeTime(value = "") {
  if (!value) {
    return "";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  const diffMs = Date.now() - date.getTime();
  const absMs = Math.abs(diffMs);
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;
  if (absMs < minute) {
    return "Just now";
  }
  if (absMs < hour) {
    const count = Math.max(1, Math.round(absMs / minute));
    return `${count}m ago`;
  }
  if (absMs < day) {
    const count = Math.max(1, Math.round(absMs / hour));
    return `${count}h ago`;
  }
  if (absMs < 7 * day) {
    const count = Math.max(1, Math.round(absMs / day));
    return `${count}d ago`;
  }
  return notificationDateTime(value);
}

function notificationAgeInDays(value = "") {
  if (!value) {
    return 0;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 0;
  }
  return Math.floor((Date.now() - date.getTime()) / (24 * 60 * 60 * 1000));
}

function notificationAgeDuration(value = "") {
  const relative = notificationRelativeTime(value);
  return relative.replace(/\s?ago$/i, "");
}

function approvalRequestDetails(notification = {}) {
  const related = notification.related || {};
  const metadata = notification.metadata || {};
  const requestType = String(related.requestType || metadata.requestType || "").toLowerCase();
  const requestLabel = requestType === "cancellation"
    ? "Cancellation request"
    : requestType === "time_change"
      ? "Time change request"
      : "New class request";
  const reason = related.reason || metadata.reason || "";
  const requestedStartsAt = notificationDateTime(metadata.requestedStartsAt || related.requestedStartsAt || metadata.startsAt || related.startsAt || "");
  const requestedEndsAt = notificationDateTime(metadata.requestedEndsAt || related.requestedEndsAt || metadata.endsAt || related.endsAt || "");
  return {
    chapterName: related.chapterName || metadata.chapterName || "",
    eventTitle: related.eventTitle || metadata.eventTitle || displayNotificationText(notification.subject || "Class request"),
    reason,
    requestLabel,
    requestedEndsAt,
    requestedStartsAt,
    requestType,
    startsAt: notificationDateTime(metadata.startsAt || related.startsAt || ""),
    submittedBy: metadata.createdByName || related.createdByName || notification.recipientEmail || "Chapter lead"
  };
}

export function NotificationsPage({
  loading,
  members = [],
  notifications = [],
  onReviewEventApproval,
  onUpdateNotificationStatus,
  selectedRange,
  setNotificationFilters,
  updatingNotificationId,
  workspaceMode = "owner"
}) {
  const chapterWorkspace = workspaceMode === "chapter";
  const [activeTab, setActiveTab] = useState(() => {
    if (typeof window === "undefined") {
      return "queued";
    }
    const hash = window.location.hash.replace("#notifications-", "");
    return ["queued", "reviewed", "archived"].includes(hash) ? hash : "queued";
  });
  const [pendingNotificationAction, setPendingNotificationAction] = useState(null);
  const [notificationDecisionNote, setNotificationDecisionNote] = useState("");
  const [notificationFeedback, setNotificationFeedback] = useState("");
  const [showAllNotifications, setShowAllNotifications] = useState(false);
  const memberLookup = useMemo(() => buildMemberLookup(members), [members]);
  const visibleMessageFeed = useMemo(
    () => collapseNotificationFeed(notifications.filter((notification) => shouldShowNotification(notification, chapterWorkspace))),
    [chapterWorkspace, notifications]
  );
  const grouped = useMemo(() => {
    return {
      queued: visibleMessageFeed.filter((notification) => !["reviewed", "archived"].includes(notification.status || "queued")),
      reviewed: visibleMessageFeed.filter((notification) => notification.status === "reviewed"),
      archived: visibleMessageFeed.filter((notification) => notification.status === "archived")
    };
  }, [visibleMessageFeed]);
  const tabOptions = [
    ["queued", "Unread"],
    ["reviewed", "Read"],
    ["archived", "Archived"]
  ];
  const visibleNotifications = grouped[activeTab] || [];
  const displayedNotifications = showAllNotifications ? visibleNotifications : visibleNotifications.slice(0, 10);
  useEffect(() => {
    function syncTabFromHash() {
      const hash = window.location.hash.replace("#notifications-", "");
      const allowedTabs = ["queued", "reviewed", "archived"];
      if (allowedTabs.includes(hash)) {
        setActiveTab(hash);
      }
    }

    syncTabFromHash();
    window.addEventListener("hashchange", syncTabFromHash);
    return () => window.removeEventListener("hashchange", syncTabFromHash);
  }, []);

  useEffect(() => {
    if (!["queued", "reviewed", "archived"].includes(activeTab)) {
      setActiveTab("queued");
    }
  }, [activeTab]);

  function switchNotificationTab(value) {
    setActiveTab(value);
    setShowAllNotifications(false);
    setPendingNotificationAction(null);
    setNotificationDecisionNote("");
    setNotificationFeedback("");
    setNotificationFilters?.((current) => ({ ...current, status: "" }));
    if (typeof window !== "undefined") {
      window.history.replaceState(null, "", `#notifications-${value}`);
    }
  }

  function openNotificationReview(notification, action, handler) {
    const subject = displayNotificationText(notification.subject || ownerNotificationLabel(notification.type));
    setPendingNotificationAction({
      action,
      handler,
      notificationId: notification.notificationId,
      subject
    });
    setNotificationDecisionNote("");
  }

  function closeNotificationReview() {
    setPendingNotificationAction(null);
    setNotificationDecisionNote("");
  }

  function updateLogicalNotificationStatus(notification, status) {
    const ids = notification.duplicateNotificationIds?.length
      ? notification.duplicateNotificationIds
      : [notification.notificationId].filter(Boolean);
    return Promise.all(ids.map((id) => onUpdateNotificationStatus(id, status)));
  }

  async function handleDirectStatusUpdate(notification, status) {
    if (!notification?.notificationId || updatingNotificationId === notification.notificationId) {
      return;
    }
    await updateLogicalNotificationStatus(notification, status);
    if (status === "reviewed") {
      switchNotificationTab("reviewed");
    } else if (status === "archived") {
      switchNotificationTab("archived");
    }
    setPendingNotificationAction(null);
  }

  async function confirmReviewedNotificationAction() {
    if (pendingNotificationAction?.handler) {
      await pendingNotificationAction.handler(notificationDecisionNote.trim());
    }
    setNotificationFeedback(`${pendingNotificationAction?.action || "Action"} complete.`);
    setPendingNotificationAction(null);
    setNotificationDecisionNote("");
  }

  return (
    <section className={`panel page-panel notification-page-panel${chapterWorkspace ? " is-chapter-workspace" : ""}`}>
      <div className="notification-tabs">
        {tabOptions.map(([value, label]) => (
          <button
            type="button"
            className={activeTab === value ? "is-active" : ""}
            key={value}
            onClick={() => switchNotificationTab(value)}
          >
            {label}{value === "settings" ? "" : ` (${grouped[value]?.length || 0})`}
          </button>
        ))}
      </div>

      {notificationFeedback && (
        <div className="status-strip notification-feedback-strip">{notificationFeedback}</div>
      )}

      {loading && <div className="empty-state">Loading notifications...</div>}
      {!loading && visibleMessageFeed.length === 0 && (
        <div className="empty-state">No messages yet.</div>
      )}
      {!loading && visibleMessageFeed.length > 0 && visibleNotifications.length === 0 && (
        <div className="empty-state">{emptyNotificationCopy(activeTab)}</div>
      )}
      {!loading && visibleNotifications.length > 0 && (
        <div className="notification-list">
          {displayedNotifications.map((notification) => {
            const memberHref = chapterWorkspace ? memberNotificationHref(notification, selectedRange) : "";
            const eventHref = eventNotificationHref(notification, selectedRange, chapterWorkspace);
            const membershipHref = chapterWorkspace ? "" : ownerMembershipNotificationHref(notification, selectedRange);
            const memberMessage = Boolean(memberHref);
            const memberDetails = memberNotificationDetails(notification, memberLookup);
            const createdAtLabel = notificationDateTime(notification.createdAt);
            const notificationType = String(notification.type || "").toLowerCase();
            const eventId = notification.related?.eventId || notification.metadata?.eventId || "";
            const notificationStatus = notification.status || "queued";
            const approvalPending = notificationType === "event.approval_requested" &&
              (notification.metadata?.approvalStatus || "pending") === "pending" &&
              !["reviewed", "archived"].includes(notificationStatus);
            const approvalDetails = approvalRequestDetails(notification);
            const visualState = notificationVisualState(notification);
            const memberRosterCopy = memberDetails.chapter
              ? `Added to your roster for ${memberDetails.chapter}.`
              : "Added to your roster.";
            const rowTimeLabel = notification.status === "reviewed" && notification.reviewedAt
              ? `Read ${notificationRelativeTime(notification.reviewedAt)}`
              : notification.status === "archived" && notification.archivedAt
                ? `Archived ${notificationRelativeTime(notification.archivedAt)}`
                : notificationAgeInDays(notification.createdAt) >= 2
                  ? `Unread for ${notificationAgeDuration(notification.createdAt)}`
                  : notificationRelativeTime(notification.createdAt) || createdAtLabel;
            const staleUnread = visualState === "unread" && notificationAgeInDays(notification.createdAt) >= 2;
            const typeClass = notificationType.replace(/[^a-z0-9]+/g, "-");
            return (
              <article className={`notification-row is-${visualState}${typeClass ? ` is-type-${typeClass}` : ""}${staleUnread ? " is-stale-unread" : ""}${memberMessage ? " is-member-message" : ""}${approvalPending ? " is-approval-request" : ""}`} key={notification.notificationId}>
                <div className="notification-message-copy">
                  {approvalPending ? (
                    <div className="notification-approval-copy">
                      <span className="notification-message-type">Approval pending</span>
                      <strong>{displayNotificationText(approvalDetails.eventTitle)}</strong>
                      <div className="notification-approval-meta">
                        <span>{approvalDetails.requestLabel}</span>
                        {approvalDetails.chapterName && <span>{approvalDetails.chapterName}</span>}
                        <span>{approvalDetails.submittedBy}</span>
                        {approvalDetails.startsAt && <span>{approvalDetails.startsAt}</span>}
                      </div>
                      {approvalDetails.requestedStartsAt && approvalDetails.requestType === "time_change" && (
                        <small>Requested time: {approvalDetails.requestedStartsAt}{approvalDetails.requestedEndsAt ? ` - ${approvalDetails.requestedEndsAt}` : ""}</small>
                      )}
                      {approvalDetails.reason && <small>Reason: {approvalDetails.reason}</small>}
                    </div>
                  ) : memberMessage ? (
                    <>
                      <span className="notification-message-type">New chapter member</span>
                      <strong>{memberDetails.name}</strong>
                      <span className="notification-message-summary">
                        {memberRosterCopy}
                      </span>
                      {memberDetails.email && (
                        <span className="notification-message-meta">{memberDetails.email}</span>
                      )}
                    </>
                  ) : (
                    <>
                      {notificationType === "event.signed_off" && (
                        <span className="notification-message-type">Class signed off</span>
                      )}
                      <strong>{displayNotificationText(notification.subject || ownerNotificationLabel(notification.type))}</strong>
                      {(notification.related?.chapterName || notification.metadata?.chapterName) && (
                        <span className="chapter-context-badge">
                          {notification.related?.chapterName || notification.metadata?.chapterName}
                        </span>
                      )}
                      <span>{displayNotificationText(notification.body)}</span>
                    </>
                  )}
                </div>
                <div className="notification-row-side">
                  <span className={`status-badge notification-state is-${visualState}`}>
                    {chapterWorkspace
                      ? chapterNotificationStatusLabel(notification.status || "queued")
                      : notificationStatusLabel(notification.status || "queued")}
                  </span>
                  <span className="notification-time">{rowTimeLabel}</span>
                  {!chapterWorkspace && approvalPending && eventId && onReviewEventApproval ? (
                    <div className="notification-approval-panel">
                      <span>Owner decision</span>
                      <strong>{approvalDetails.requestLabel}</strong>
                      {eventHref && (
                        <a className="notification-review-class-link" href={eventHref}>
                          View request
                        </a>
                      )}
                      <div className="notification-approval-decisions">
                        <button
                          type="button"
                          className="notification-approve-button"
                          disabled={updatingNotificationId === notification.notificationId}
                          onClick={() => openNotificationReview(notification, "Approve request", async (note) => {
                            await onReviewEventApproval(eventId, "approved", { note });
                            await updateLogicalNotificationStatus(notification, "reviewed");
                          })}
                        >
                          Approve
                        </button>
                        <button
                          type="button"
                          className="notification-reject-button"
                          disabled={updatingNotificationId === notification.notificationId}
                          onClick={() => openNotificationReview(notification, "Reject request", async (note) => {
                            await onReviewEventApproval(eventId, "rejected", { note });
                            await updateLogicalNotificationStatus(notification, "reviewed");
                          })}
                        >
                          Reject
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="row-actions notification-actions">
                      {memberHref && (
                        <a className="notification-primary-link" href={memberHref}>
                          View member
                        </a>
                      )}
                      {eventHref && (
                        <a className="notification-primary-link" href={eventHref}>
                          {notificationType === "event.signed_off" ? "View sign-off" : "Open class"}
                        </a>
                      )}
                      {membershipHref && (
                        <a className="notification-primary-link" href={membershipHref}>
                          Review application
                        </a>
                      )}
                      {!["archived", "reviewed"].includes(notification.status || "queued") && (
                        <button
                          type="button"
                          disabled={updatingNotificationId === notification.notificationId}
                          onClick={() => handleDirectStatusUpdate(notification, "reviewed")}
                        >
                          Mark read
                        </button>
                      )}
                      {notification.status !== "archived" && (
                        <button
                          type="button"
                          disabled={updatingNotificationId === notification.notificationId}
                          onClick={() => handleDirectStatusUpdate(notification, "archived")}
                        >
                          Archive
                        </button>
                      )}
                    </div>
                  )}
                  {pendingNotificationAction?.notificationId === notification.notificationId && (
                    <div className="notification-action-review">
                      <span>{pendingNotificationAction.action === "Reject request" ? "Confirm rejection" : "Confirm decision"}</span>
                      <strong>{pendingNotificationAction.action}</strong>
                      <small>{pendingNotificationAction.subject}</small>
                      <textarea
                        value={notificationDecisionNote}
                        onChange={(event) => setNotificationDecisionNote(event.target.value)}
                        placeholder={pendingNotificationAction.action === "Reject request" ? "Reason for the chapter lead" : "Optional note"}
                        rows={3}
                      />
                      <div className="row-actions">
                        <button type="button" onClick={closeNotificationReview}>Cancel</button>
                        <button
                          type="button"
                          className={pendingNotificationAction.action === "Reject request" ? "danger-button" : ""}
                          onClick={confirmReviewedNotificationAction}
                          disabled={updatingNotificationId === notification.notificationId}
                        >
                          {pendingNotificationAction.action === "Reject request" ? "Reject" : "Confirm"}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </article>
            );
          })}
          {visibleNotifications.length > displayedNotifications.length && (
            <button
              type="button"
              className="notification-show-all"
              onClick={() => setShowAllNotifications(true)}
            >
              Show all {visibleNotifications.length} records
            </button>
          )}
        </div>
      )}
    </section>
  );
}
